import { Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { Apollo, gql, MutationResult } from 'apollo-angular';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PreparerRequest } from '../../models/api/services/preparer-requests.interface';
import { PagedRequest } from '../../models/api/shared/paged/paged-request';
import { PagedResponse } from '../../models/api/shared/paged/paged-response.interface';
import { Preparer, PreparerArray } from '../../models/entities/preparer';
import { TypeSerializerUtils } from '../../utils/type-serializer.util';

@Injectable({
  providedIn: 'root'
})
export class PreparersService {
  constructor(
    private readonly apollo: Apollo
  ) {}

  addPreparer(data: PreparerRequest): Observable<MutationResult<Preparer>> {
    const mutation = gql`
      mutation addPreparer($data: PreparerRequest) {
        actors {
          preparers {
            add(data: $data) {
              id
              active
              name
              structure {
                id
                active
                name
              }
              contacts {
                id
                name
                sendMail
                mail
                phoneNumber
                comment
              }
            }
          }
        }
      }
    `;
    return this.apollo.mutate<{ actors: { preparers: { add: Preparer } } }>({
      mutation,
      variables: {
        data: {
          name: data.name,
          structureId: data.structure.id,
          contacts : [{
            id: data.contactId,
            name: data.name,
            phoneNumber: data.tel,
            mail: data.email
          }]
        }
      }
    }).pipe(map(result => ({

      data: TypeSerializerUtils.deserialize(result?.data?.actors?.preparers?.add, Preparer),
      errors: result.errors,
      extensions: result.extensions,
      loading: result.loading
    })));
  }

  updatePreparer(data: PreparerRequest): Observable<MutationResult<Preparer>> {
    const mutation = gql`
      mutation updatePreparer($id: Guid!, $data: PreparerRequest) {
        actors {
          preparers {
            update(id: $id, data: $data) {
              id
              active
              name
              structure {
                id
                active
                name
              }
              contacts {
                id
                name
                sendMail
                mail
                phoneNumber
                comment
              }
            }
          }
        }
      }
    `;

    return this.apollo.mutate<{ actors: { preparers: { update: Preparer } } }>({
      mutation,
      variables: {
        id: data.id,
        data: {
          active: data.active,
          name: data.name,
          structureId: data.structure.id,
          contacts : [{
            id: data.contactId,
            name: data.name,
            phoneNumber: data.tel,
            mail: data.email
          }]
        }
      }
    }).pipe(map(result => ({

      data: TypeSerializerUtils.deserialize(result.data?.actors?.preparers?.update, Preparer),
      errors: result.errors,
      extensions: result.extensions,
      loading: result.loading
    })));
  }

  deletePreparer(...ids: string[]): Observable<MutationResult<void>> {
    const query = `
      mutation deletePreparer(` + ids.map((_, index) => '$id' + index + ': Guid!').join(', ') + `) {
        actors {
          preparers {\n` + ids.map((_, index) => 'id' + index + ': delete (id: $id' + index + ')').join('\n') + `\n}
        }
      }
    `;

    const variables = ids.reduce((response, item, index) => {
      response['id' + index] = item;
      return response;
    }, {});

    return this.apollo.mutate<void>({
      mutation: gql(query),
      variables
    });
  }

  deleteOnePreparerHard(id: string): Observable<MutationResult<void>> {
    const mutation = gql`
      mutation deletePreparerActor($id: Guid!, $hard: Boolean) {
        actors {
          preparers {
            delete(id: $id, hard: $hard)
          }
        }
      }
    `;

    const variables = {
      id,
      hard: true
    };

    return this.apollo.mutate<void>({
      mutation,
      variables
    });
  }

  preparer(id: string): Observable<ApolloQueryResult<Preparer>> {
    const query = gql`
      query preparer($id: Guid!) {
        actors {
          preparer(id: $id) {
            id
            active
            name
            structure {
              id
              active
              name
            }
            contacts {
              id
              name
              sendMail
              mail
              phoneNumber
              comment
            }
          }
        }
      }
    `;
    return this.apollo.query<{ actors: { preparer: Preparer } }>({
      query,
      variables: { id }
    }).pipe(map(result => ({
      data: TypeSerializerUtils.deserialize(result.data?.actors?.preparer, Preparer),
      error: result.error,
      errors: result.errors,
      partial: result.partial,
      loading: result.loading,
      networkStatus: result.networkStatus
    })));
  }

  allPreparers(parameters: PagedRequest): Observable<ApolloQueryResult<PagedResponse<Preparer>>> {
    const query = gql`
      query preparers($offset: Int, $limit: Int, $sortProperty: String, $sortType: ESortType, $search: String) {
        actors {
          preparers(offset: $offset, limit: $limit, sortProperty: $sortProperty, sortType: $sortType, search: $search) {
            data {
              id
              active
              name
              structure {
                id
                active
                name
              }
              contacts {
                id
                mail
                phoneNumber
              }
            }
            metadata {
              currentPage
              currentResult
              totalPages
              totalResults
              hasMore
            }
          }
        }
      }
    `;

    return this.apollo.query<{ actors: { preparers: PagedResponse<Preparer> } }>({
      query,
      variables: {
        offset: parameters.offset,
        limit: parameters.limit,
        sortProperty: parameters.sortProperty,
        sortType: parameters.sortType,
        search: parameters.search
      }
    }).pipe(map(result => {
      const raw = result.data?.actors?.preparers;
      const response: PagedResponse<Preparer> = {
        data: TypeSerializerUtils.deserializeArr(raw.data, PreparerArray),
        metadata: raw.metadata
      };
      return {
        data: response,
        error: result.error,
        errors: result.errors,
        partial: result.partial,
        loading: result.loading,
        networkStatus: result.networkStatus
      };
    }));
  }
}
